import {CohortASchema} from '@cohort/admin-schemas/cohort';
import {DigitalAssetCollectionASchema} from '@cohort/admin-schemas/digitalAssetCollection';
import {MerchantASchema} from '@cohort/admin-schemas/merchant';
import {MAX_CAMPAIGN_SIZE, REDEEM_CODES_MAX_QUANTITY} from '@cohort/shared/constants';
import {
  asArray,
  DateSchema,
  HexColorSchema,
  LocalizedRichTextSchema,
  LocalizedStringSchema,
} from '@cohort/shared/schema/common';
import {
  CampaignAudienceSchema,
  CampaignPricingSchema,
  CampaignStatusSchema,
  CampaignTypeSchema,
  LocalizedFaqsSchema,
} from '@cohort/shared/schema/common/campaign';
import {CurrencySchema, PricesSchema} from '@cohort/shared/schema/common/currency';
import {PaginationParamsSchema} from '@cohort/shared/schema/common/pagination';
import {z} from 'zod';

export const CampaignStoreASchema = z.object({
  accentColorCode: HexColorSchema.nullable(),
  backgroundColorCode: HexColorSchema.nullable(),
  description: LocalizedRichTextSchema,
  defaultCurrency: CurrencySchema.nullable(),
  prices: PricesSchema,
  pricing: CampaignPricingSchema,
  slug: z.string().min(3).nullable(),
  shopUrl: z.string().url().nullable(),
});
export type CampaignStoreADto = z.infer<typeof CampaignStoreASchema>;

export const CreateCampaignStoreASchema = z
  .object({
    accentColorCode: HexColorSchema,
    backgroundColorCode: HexColorSchema,
    description: LocalizedRichTextSchema.or(z.record(z.never())),
    defaultCurrency: CurrencySchema.nullable(),
    prices: PricesSchema,
    pricing: CampaignPricingSchema,
    slug: z
      .string()
      .min(3)
      .refine(slug => /^[a-z0-9-]+$/u.test(slug)),
  })
  .partial();
export type CreateCampaignStoreADto = z.infer<typeof CreateCampaignStoreASchema>;

const CampaignJourneyASchema = z.object({
  imageFileKey: z.string().nullable(),
  animationFileKey: z.string().nullable(),
  description: LocalizedRichTextSchema,
  title: LocalizedStringSchema,
  optInCta: LocalizedStringSchema,
  spaceUrl: z.string().url().nullable(),
});
export type CampaignJourneyADto = z.infer<typeof CampaignJourneyASchema>;

const CreateCampaignJourneyASchema = z
  .object({
    title: LocalizedStringSchema.or(z.record(z.never())),
    imageFileKey: z.string().nullable(),
    animationFileKey: z.string().nullable(),
    description: LocalizedRichTextSchema.or(z.record(z.never())),
    optInCta: LocalizedStringSchema.or(z.record(z.never())),
  })
  .partial();
export type CreateCampaignJourneyADto = z.infer<typeof CreateCampaignJourneyASchema>;

export const CreateCampaignAirdropASchema = z.object({}).partial();
export type CreateCampaignAirdropADto = z.infer<typeof CreateCampaignAirdropASchema>;

const RedeemCodeStatusASchema = z.enum(['available', 'consumed', 'expired']);
export type RedeemCodeStatusADto = z.infer<typeof RedeemCodeStatusASchema>;

export const RedeemCodeASchema = z.object({
  id: z.string().uuid(),
  campaignId: z.string().uuid(),
  code: z.string().uuid(),
  status: RedeemCodeStatusASchema,
  tokenId: z.number().int().min(0).nullable(),
  imageFileKey: z.string(),
});
export type RedeemCodeADto = z.infer<typeof RedeemCodeASchema>;

export const CampaignRevenueASchema = z.array(
  z.object({
    currency: CurrencySchema,
    amount: z.number().int().min(0),
  })
);
export type CampaignRevenueADto = z.infer<typeof CampaignRevenueASchema>;

export const CampaignASchema = z.object({
  id: z.string().uuid(),
  merchant: MerchantASchema,
  status: CampaignStatusSchema,
  totalSupply: z.number().int().min(1).max(MAX_CAMPAIGN_SIZE).nullable(),
  availableSupply: z.number().int().min(0).nullable(),
  faqs: LocalizedFaqsSchema,
  tosFileKey: z.string().nullable(),
  tosUrl: z.string().url().nullable(),
  internalName: z.string().min(3),
  cohortId: z.string().uuid().nullable(),
  cohort: CohortASchema.nullable(),
  numOrdered: z.number().int().min(0),
  type: CampaignTypeSchema.nullable(),
  audience: CampaignAudienceSchema.nullable(),
  store: CampaignStoreASchema.nullable(),
  journey: CampaignJourneyASchema.nullable(),
  createdAt: DateSchema,
  updatedAt: DateSchema,
  digitalAssetCollection: DigitalAssetCollectionASchema.nullable(),
  isArchived: z.boolean(),
});
export type CampaignADto = z.infer<typeof CampaignASchema>;

export const CreateCampaignASchema = z
  .object({
    audience: CampaignAudienceSchema.nullable(),
    digitalAssetCollectionId: z.string().uuid(),
    cohortId: z.string().uuid().nullable(),
    faqs: LocalizedFaqsSchema,
    slug: z
      .string()
      .min(3)
      .refine(slug => /^[a-z0-9-]+$/u.test(slug)),
    store: CreateCampaignStoreASchema.nullable(),
    journey: CreateCampaignJourneyASchema.nullable(),
    airdrop: CreateCampaignAirdropASchema.nullable(),
    tosFileKey: z.string().nullable(),
    totalSupply: z.number().int().min(1).max(MAX_CAMPAIGN_SIZE).nullable(),
    type: CampaignTypeSchema.nullable(),
  })
  .partial()
  .extend({
    internalName: z.string().min(3),
  });
export type CreateCampaignADto = z.infer<typeof CreateCampaignASchema>;

export const PatchCampaignASchema = CreateCampaignASchema.partial();
export type PatchCampaignADto = z.infer<typeof PatchCampaignASchema>;

export const CampaignSlugAvailabilityASchema = z.object({
  available: z.boolean(),
});
export type CampaignSlugAvailabilityADto = z.infer<typeof CampaignSlugAvailabilityASchema>;

export const GetCampaignsPriceParamASchema = z.enum(['free', 'paid']);
export type GetCampaignsPriceParamADto = z.infer<typeof GetCampaignsPriceParamASchema>;

export const GetCampaignsSortParamASchema = z.enum([
  'updatedAt',
  'numOrdered',
  'internalName',
  'totalSupply',
]);
export type GetCampaignsSortParamADto = z.infer<typeof GetCampaignsSortParamASchema>;

export const CampaignsVisibilityParamSchema = z.enum(['all', 'archived', 'visible']);
export type CampaignsVisibilityParamADto = z.infer<typeof CampaignsVisibilityParamSchema>;

export const GetCampaignsParamsASchema = PaginationParamsSchema.extend({
  status: z.preprocess(asArray, z.array(CampaignStatusSchema).optional()),
  pricing: z.preprocess(asArray, z.array(CampaignPricingSchema).optional()),
  type: z.preprocess(asArray, z.array(CampaignTypeSchema).optional()),
  visibility: CampaignsVisibilityParamSchema.optional(),
  orderBy: GetCampaignsSortParamASchema.optional(),
  digitalAssetCollectionId: z.string().uuid().optional(),
});
export type GetCampaignsParamsADto = z.infer<typeof GetCampaignsParamsASchema>;

export const CreateRedeemCodesResponseASchema = z.array(RedeemCodeASchema);
export type CreateRedeemCodesResponseADto = z.infer<typeof CreateRedeemCodesResponseASchema>;

export const CreateRedeemCodesDataASchema = z.object({
  quantity: z.number().int().min(1).max(REDEEM_CODES_MAX_QUANTITY),
});
export type CreateRedeemCodesDataADto = z.infer<typeof CreateRedeemCodesDataASchema>;
